import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, AlertCircle, Bike, Wrench, Gauge, Zap, Palette,
} from "lucide-react";
import { SiBmw, SiDucati, SiHonda, SiHusqvarna, SiKtm, SiSuzuki, SiYamahamotorcorporation as SiYamaha } from "react-icons/si";
import PageHeader from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { SITE } from "../lib/site";
import {
  MOTO_BRANDS, MOTO_MAKES, MOTO_BUILD_TYPES, MOTO_YEARS, MOTO_GALLERY,
} from "../lib/motorcycles";
import {
  HarleyLogo, KawasakiLogo, TriumphLogo, IndianLogo, PolarisLogo,
  CanAmLogo, ApriliaLogo, ArcticCatLogo,
} from "../components/MotoBrandLogos";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Brand card configuration — mix of svg icons + custom wordmarks
const BRAND_CARDS = [
  { key: "harley", name: "Harley-Davidson", Logo: HarleyLogo, blurb: "Softail, Sportster, Road/Street Glide — full custom, bobbers, choppers, engine work." },
  { key: "bmw", name: "BMW Motorrad", Logo: SiBmw, blurb: "R nineT, GS, S 1000 RR — factory-grade diagnostics, ECU coding, café racers." },
  { key: "ducati", name: "Ducati", Logo: SiDucati, blurb: "Panigale, Monster, Streetfighter, Multistrada — desmo service, tuning, track prep." },
  { key: "honda", name: "Honda", Logo: SiHonda, blurb: "CBR, CB, Africa Twin, Rebel, Gold Wing — plus ATVs (TRX400X, Raptor)." },
  { key: "yamaha", name: "Yamaha", Logo: SiYamaha, blurb: "R1, MT-09, Ténéré 700, XSR — plus YFZ450R, Raptor 700, Grizzly ATVs." },
  { key: "kawasaki", name: "Kawasaki", Logo: KawasakiLogo, blurb: "Ninja ZX-6R/10R, Z900, Versys, Vulcan — plus Brute Force ATVs." },
  { key: "suzuki", name: "Suzuki", Logo: SiSuzuki, blurb: "GSX-R, Hayabusa, SV650, V-Strom — plus KingQuad ATVs." },
  { key: "triumph", name: "Triumph", Logo: TriumphLogo, blurb: "Bonneville, Street Triple, Tiger, Rocket 3 — modern classic builds a specialty." },
  { key: "ktm", name: "KTM", Logo: SiKtm, blurb: "Duke, Adventure, RC — race-tuned by people who ride hard." },
  { key: "indian", name: "Indian", Logo: IndianLogo, blurb: "Scout, Chief, Challenger — cruiser mods, exhaust, air ride." },
  { key: "aprilia", name: "Aprilia", Logo: ApriliaLogo, blurb: "RSV4, Tuono, RS 660 — Italian sport bike service and tune." },
  { key: "husqvarna", name: "Husqvarna", Logo: SiHusqvarna, blurb: "Svartpilen, Norden 901, dirt & enduro — top-end work and tuning." },
  { key: "polaris", name: "Polaris", Logo: PolarisLogo, blurb: "RZR, Sportsman, Ranger, General — UTV/ATV builds and repair." },
  { key: "canam", name: "Can-Am", Logo: CanAmLogo, blurb: "Maverick X3, Outlander, Renegade, Spyder, Ryker — 3-wheelers included." },
  { key: "arcticcat", name: "Arctic Cat", Logo: ArcticCatLogo, blurb: "Alterra, Wildcat, Prowler — service and performance." },
];

const MOTO_SERVICES = [
  { code: "01", icon: Wrench, t: "Service & repair", d: "Fluids, valves, chains, brakes, suspension, tires. Everything a bike needs to stay a bike." },
  { code: "02", icon: Zap, t: "Performance & tuning", d: "ECU flashes, exhaust, intake, cams. Dyno-validated and street-legal (or not — your call)." },
  { code: "03", icon: Bike, t: "Custom builds", d: "Café racer, bobber, chopper, scrambler, tracker. From napkin sketch to keys-in-hand." },
  { code: "04", icon: Gauge, t: "Engine rebuilds", d: "Top-end, bottom-end, forged rebuilds. Vintage or modern, we've seen the guts." },
  { code: "05", icon: Palette, t: "Paint & bodywork", d: "Custom paint, tank work, seat re-covers, one-off fabrication where needed." },
];

const TIME_SLOTS = [
  { value: "morning", label: "Morning (9am – 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm – 3pm)" },
  { value: "evening", label: "Late afternoon (3pm – 5pm)" },
  { value: "flexible", label: "Flexible — call to confirm" },
];

const Field = ({ label, required, children, hint }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60">
      {label}{required && <span className="text-[#FF3B30]"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <div className="mt-1 text-white/40 text-xs">{hint}</div>}
  </label>
);

const inputCls =
  "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#FF3B30] outline-none text-white text-base px-4 py-3 transition-colors placeholder:text-white/30";

const sizeClass = (s) => {
  if (s === "lg") return "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto";
  return "md:col-span-1 md:row-span-1 aspect-[4/3]";
};

export default function Motorcycles() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    vehicle_kind: "motorcycle",
    year: "", make: "", model: "", model_other: "", make_other: "",
    build_type: MOTO_BUILD_TYPES[0],
    modifications: "", budget: "",
    preferred_date: "", preferred_time: "flexible",
    notes: "",
  });
  const [state, setState] = useState({ status: "idle", error: null, id: null });

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "make") { next.model = ""; next.model_other = ""; }
      return next;
    });
  };

  const isOtherMake = form.make === "Other (write-in)";
  const models = useMemo(
    () => (form.make && !isOtherMake ? (MOTO_BRANDS[form.make]?.models || []) : []),
    [form.make, isOtherMake]
  );
  const isOtherModel = form.model === "Other / unknown" || (models.length && form.model?.startsWith("Other"));

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === "submitting") return;
    setState({ status: "submitting", error: null, id: null });
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        vehicle_kind: form.vehicle_kind,
        year: form.year,
        make: isOtherMake ? (form.make_other || "Other") : form.make,
        model: (isOtherMake || isOtherModel) ? (form.model_other || form.model || "Other") : form.model,
        build_type: form.build_type,
        modifications: form.modifications,
        budget: form.budget || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || "flexible",
        notes: form.notes || null,
      };
      const res = await fetch(`${API}/moto-builds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setState({ status: "success", error: null, id: data.id });
    } catch (err) {
      setState({ status: "error", error: err.message || "Something went wrong", id: null });
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="page-motorcycles">
      <Seo
        path="/motorcycles"
        title="Motorcycles & ATVs — Custom Builds, Service & Tuning"
        description="Harley-Davidson, BMW Motorrad, Ducati, Honda, Yamaha, Kawasaki, Suzuki, Triumph, KTM, Indian, Aprilia, Polaris, Can-Am. Custom builds, café racers, bobbers, engine work and ATV service in Hermiston, OR. Photo documented start to finish."
      />

      <PageHeader
        code="// 08"
        kicker="Motorcycles & ATVs"
        title={<>Two wheels.<br />(Or four.)</>}
        subtitle="Cars are the day job — bikes and ATVs are the reason. Custom builds, engine work, service and tuning for every major marque. Public gallery below, custom build form at the bottom."
        testId="moto-header"
      />

      {/* BIG BRAND LOGOS GRID */}
      <section data-testid="moto-brands-grid" className="px-6 md:px-12 lg:px-24 py-20 md:py-24 border-b border-white/10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// marques we build</span>
            <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
              Fifteen brands.<br />Cars, bikes, ATVs.
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
            Don't see yours? Bring it anyway — we still work on everything else. No job is too small.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {BRAND_CARDS.map((b) => {
            const Logo = b.Logo;
            const isTextLogo = typeof Logo === "function" && Logo.length === 1; // custom wordmark component
            return (
              <div
                key={b.key}
                data-testid={`moto-brand-${b.key}`}
                className="group bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 md:p-10 aspect-square flex flex-col items-center justify-center text-center relative overflow-hidden"
              >
                <div className="text-white/45 group-hover:text-white transition-colors">
                  {isTextLogo ? (
                    <Logo className="text-3xl md:text-4xl" />
                  ) : (
                    <Logo className="w-14 h-14 md:w-16 md:h-16" />
                  )}
                </div>
                <div className="mt-4 font-display font-bold uppercase tracking-tight text-xs md:text-sm text-white/60 group-hover:text-white transition-colors">
                  {b.name}
                </div>
                <div className="absolute inset-x-4 bottom-4 text-[10px] text-white/40 opacity-0 group-hover:opacity-100 transition-opacity leading-snug">
                  {b.blurb}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="moto-services" className="px-6 md:px-12 lg:px-24 py-20 md:py-24 border-b border-white/10">
        <div className="mb-10">
          <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// bike & ATV services</span>
          <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
            From routine to<br />ridiculous.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {MOTO_SERVICES.map((s) => (
            <div
              key={s.code}
              data-testid={`moto-service-${s.code}`}
              className="bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-6 lg:p-8 min-h-[220px] flex flex-col justify-between"
            >
              <div>
                <span className="font-mono-tech text-xs text-[#FF3B30] tracking-[0.25em]">{s.code}</span>
                <s.icon className="w-6 h-6 text-white/60 mt-4" />
              </div>
              <div>
                <div className="font-display font-black uppercase tracking-tight text-lg md:text-xl">{s.t}</div>
                <div className="mt-2 text-white/55 text-sm leading-relaxed">{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PUBLIC GALLERY */}
      <section data-testid="moto-gallery" className="px-6 md:px-12 lg:px-24 py-20 md:py-24 border-b border-white/10">
        <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// public builds</span>
            <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
              Custom-built.<br />Out the door.
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
            A slice of what's rolled out. Café racers, bobbers, factory sport bikes, custom cruisers. Every job photo documented start to finish.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[220px] md:auto-rows-[260px] gap-3 md:gap-4">
          {MOTO_GALLERY.map((g, i) => (
            <figure
              key={i}
              data-testid={`moto-gallery-${i}`}
              className={`group relative overflow-hidden border border-white/10 bg-[#1A1F24] ${sizeClass(g.size)}`}
            >
              <img
                src={g.url}
                alt={g.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent opacity-90" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5 flex items-center justify-between">
                <span className="font-display font-bold uppercase tracking-tight text-sm md:text-base">{g.tag}</span>
                <span className="font-mono-tech text-[10px] text-[#FF3B30] tracking-[0.3em] uppercase">{String(i + 1).padStart(2, "0")}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CUSTOM BUILD FORM */}
      <section data-testid="moto-build-form-section" className="px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="mb-10">
          <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// submit an idea</span>
          <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
            Got a build in your head?<br />Send it over.
          </h2>
          <p className="mt-4 text-white/65 max-w-2xl leading-relaxed">
            Custom build ideas for motorcycles or ATVs / UTVs. The more you tell us — donor bike, direction, references, budget — the faster we can turn it into a real quote. Every build we take on is photo documented start to finish so you see exactly what your money bought.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-8">
            <div className="bg-[#1A1F24] border border-white/10 p-6">
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60 mb-2">Photo documented</div>
              <p className="text-white/70 text-sm leading-relaxed">
                Every custom build is photographed at every stage — teardown, mid-build, finished. You get the album with the invoice. No mystery, no "trust us."
              </p>
            </div>
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60 mb-2">Prefer to call?</div>
              <a
                href={`tel:${SITE.contact.phone}`}
                data-testid="moto-tel-link"
                className="font-display font-black uppercase tracking-tight text-2xl text-white hover:text-[#FF3B30] transition-colors"
              >
                {SITE.contact.phone}
              </a>
              <div className="mt-1 text-white/50 text-sm">{SITE.contact.address}</div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {state.status === "success" ? (
              <div data-testid="moto-success" className="bg-[#0F1A12] border border-emerald-500/30 p-8 md:p-12">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-emerald-400">// build idea received</div>
                <h3 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                  Got it. We'll be in touch.
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed max-w-xl">
                  We'll review the build, price the parts, and call you at the number you provided. Usually inside one business day.
                </p>
                <div className="mt-6 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/40">
                  Reference: <span className="text-white/70">{state.id}</span>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => { setState({ status: "idle", error: null, id: null }); setForm((f) => ({ ...f, modifications: "", notes: "" })); }}
                    data-testid="moto-new-btn"
                    className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 transition-colors"
                  >
                    Submit another
                  </button>
                  <Link to="/" className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 transition-colors">
                    Back to home <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <form data-testid="moto-form" onSubmit={submit} className="bg-[#0E0E0E] border border-white/10 p-6 md:p-10 space-y-6">
                {/* contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name" required>
                    <input data-testid="moto-input-name" className={inputCls} value={form.name} onChange={set("name")} required placeholder="First and last" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="moto-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} required type="tel" placeholder="(541) 555-0199" />
                  </Field>
                </div>
                <Field label="Email" required>
                  <input data-testid="moto-input-email" className={inputCls} value={form.email} onChange={set("email")} required type="email" placeholder="you@example.com" />
                </Field>

                {/* vehicle kind + year/make/model */}
                <div className="border-t border-white/10 pt-6">
                  <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase mb-4">// the machine</div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Field label="Type" required>
                      <select data-testid="moto-input-kind" className={inputCls} value={form.vehicle_kind} onChange={set("vehicle_kind")} required>
                        <option value="motorcycle">Motorcycle</option>
                        <option value="atv">ATV / UTV</option>
                      </select>
                    </Field>
                    <Field label="Year" required>
                      <select data-testid="moto-input-year" className={inputCls} value={form.year} onChange={set("year")} required>
                        <option value="">Select…</option>
                        {MOTO_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </Field>
                    <Field label="Make" required>
                      <select data-testid="moto-input-make" className={inputCls} value={form.make} onChange={set("make")} required>
                        <option value="">Select…</option>
                        {MOTO_MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Model" required>
                      {isOtherMake ? (
                        <input data-testid="moto-input-model-other" className={inputCls} value={form.model_other} onChange={set("model_other")} required placeholder="e.g. Bantam" />
                      ) : (
                        <select data-testid="moto-input-model" className={inputCls} value={form.model} onChange={set("model")} required disabled={!form.make}>
                          <option value="">{form.make ? "Select…" : "Pick a make first"}</option>
                          {models.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      )}
                    </Field>
                  </div>
                  {isOtherMake && (
                    <div className="mt-4">
                      <Field label="Make (write-in)" required>
                        <input data-testid="moto-input-make-other" className={inputCls} value={form.make_other} onChange={set("make_other")} required placeholder="e.g. Royal Enfield" />
                      </Field>
                    </div>
                  )}
                  {isOtherModel && !isOtherMake && (
                    <div className="mt-4">
                      <Field label="Model (write-in)">
                        <input data-testid="moto-input-model-other" className={inputCls} value={form.model_other} onChange={set("model_other")} placeholder="Specific model" />
                      </Field>
                    </div>
                  )}
                </div>

                {/* build type + budget */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Build type" required>
                    <select data-testid="moto-input-buildtype" className={inputCls} value={form.build_type} onChange={set("build_type")} required>
                      {MOTO_BUILD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                  <Field label="Budget range" hint="Optional — keeps things realistic">
                    <input data-testid="moto-input-budget" className={inputCls} value={form.budget} onChange={set("budget")} placeholder="$4k – $8k" />
                  </Field>
                </div>

                <Field label="Modifications / build details" required hint="What direction, parts, references — the more the better.">
                  <textarea
                    data-testid="moto-input-mods"
                    className={`${inputCls} min-h-[160px] resize-y leading-relaxed`}
                    value={form.modifications}
                    onChange={set("modifications")}
                    required
                    placeholder={`Example:
- Café racer conversion on a 1978 CB750
- Clip-ons, rear-sets, subframe hoop
- Custom leather seat, 2-into-1 exhaust
- Aim: bare-metal tank, brushed engine covers`}
                  />
                </Field>

                <div className="border-t border-white/10 pt-6">
                  <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase mb-4">// schedule a consult</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Preferred date" hint="We'll confirm by phone">
                      <input data-testid="moto-input-date" type="date" min={today} className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
                    </Field>
                    <Field label="Preferred time">
                      <select data-testid="moto-input-time" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")}>
                        {TIME_SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>

                <Field label="Anything else?" hint="Optional">
                  <textarea data-testid="moto-input-notes" className={`${inputCls} min-h-[100px] resize-y`} value={form.notes} onChange={set("notes")} placeholder="Links to reference builds, deadlines, etc." />
                </Field>

                {state.status === "error" && (
                  <div data-testid="moto-error" className="flex items-start gap-3 bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF8A82] p-4">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="text-sm">{state.error}</div>
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
                  <div className="text-white/45 text-xs max-w-md leading-relaxed">
                    Submitting lets us contact you at the phone and email above to talk through the build.
                  </div>
                  <button
                    data-testid="moto-submit-btn"
                    type="submit"
                    disabled={state.status === "submitting"}
                    className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5722] disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
                  >
                    {state.status === "submitting" ? "Sending…" : "Send my build idea"}
                    {state.status !== "submitting" && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
